import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'
import {useResizeObserver} from '@github-ui/use-resize-observer'
import type {RefObject} from 'react'
import {useCallback, useEffect, useRef, useState} from 'react'

type ScrollBehaviorWithInstant = ScrollBehavior | 'instant'

export type UseAutoScrollOptions = {
  /** The scroll container element. */
  containerRef: RefObject<HTMLElement | null>
  /** Uses the document scroller instead of containerRef. Default container. */
  scrollTarget?: 'container' | 'document'
  /** Resets scroll tracking state when this value changes. */
  resetKey?: string
  /** Enables or disables auto-scroll behavior. */
  enabled?: boolean
  /** Distance in px from the bottom that still counts as pinned. Default 64. */
  bottomThreshold?: number
  /**
   * When false, content growth will not auto-scroll until the user explicitly
   * scrolls to bottom or returns near the bottom. Useful for preserving page-load position.
   */
  autoFollowOnMount?: boolean
}

type ScrollToBottomOptions = {
  behavior?: ScrollBehaviorWithInstant
  respectReducedMotion?: boolean
}

function getBottomDistance(container: HTMLElement): number {
  return container.scrollHeight - container.scrollTop - container.clientHeight
}

function isNearBottom(container: HTMLElement, threshold: number): boolean {
  return getBottomDistance(container) <= threshold
}

function isAtActualBottom(container: HTMLElement): boolean {
  return getBottomDistance(container) <= 1
}

function setScrollTopImmediately(container: HTMLElement, scrollTop: number): number {
  const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight)
  const nextScrollTop = Math.max(0, Math.min(scrollTop, maxScrollTop))
  const previousScrollBehavior = container.style.scrollBehavior
  container.style.scrollBehavior = 'auto'
  // The rule classifies this Safari-compatible assignment as a read; all layout reads are above.
  // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-after-write
  container.scrollTop = nextScrollTop
  container.style.scrollBehavior = previousScrollBehavior
  return nextScrollTop
}

function scrollContainer(container: HTMLElement, top: number, behavior: ScrollBehavior) {
  container.scrollTo({top, behavior})
}

export function useAutoScroll({
  containerRef,
  resetKey,
  enabled = true,
  bottomThreshold = 64,
  autoFollowOnMount = true,
  scrollTarget = 'container',
}: UseAutoScrollOptions) {
  const prefersReducedMotion = usePrefersReducedMotion()
  const [isAtBottom, setIsAtBottom] = useState(false)
  const autoFollowRef = useRef(autoFollowOnMount)
  const lastObservedScrollTopRef = useRef(0)
  const scheduledScrollFrameRef = useRef<number | undefined>(undefined)
  const scheduledScrollBehaviorRef = useRef<ScrollBehaviorWithInstant | undefined>(undefined)
  const smoothScrollActiveRef = useRef(false)
  const contentAttachedRef = useRef(false)
  const userScrollIntentRef = useRef(false)
  const userScrollIntentTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const lastObservedScrollHeightRef = useRef(0)
  const scrollRangeShrankRef = useRef(false)

  const getScrollContainer = useCallback(() => {
    if (scrollTarget === 'document') {
      return (document.scrollingElement as HTMLElement | null) ?? document.documentElement
    }
    return containerRef.current
  }, [containerRef, scrollTarget])

  const cancelScheduledScroll = useCallback(() => {
    if (scheduledScrollFrameRef.current !== undefined) {
      cancelAnimationFrame(scheduledScrollFrameRef.current)
      scheduledScrollFrameRef.current = undefined
    }
    scheduledScrollBehaviorRef.current = undefined
  }, [])

  const clearUserScrollIntent = useCallback(() => {
    if (userScrollIntentTimeoutRef.current !== undefined) {
      clearTimeout(userScrollIntentTimeoutRef.current)
      userScrollIntentTimeoutRef.current = undefined
    }
    userScrollIntentRef.current = false
  }, [])

  const markUserScrollIntent = useCallback(() => {
    userScrollIntentRef.current = true
    if (userScrollIntentTimeoutRef.current !== undefined) clearTimeout(userScrollIntentTimeoutRef.current)
    userScrollIntentTimeoutRef.current = setTimeout(() => {
      userScrollIntentTimeoutRef.current = undefined
      userScrollIntentRef.current = false
    }, 1000)
  }, [])

  const handleWheelIntent = useCallback(
    (event: WheelEvent) => {
      if (event.deltaY < 0) markUserScrollIntent()
    },
    [markUserScrollIntent],
  )

  const addUserScrollIntentListeners = useCallback(
    (target: HTMLElement | Window) => {
      target.addEventListener('wheel', handleWheelIntent as EventListener, {passive: true})
      target.addEventListener('touchmove', markUserScrollIntent, {passive: true})
      target.addEventListener('pointerdown', markUserScrollIntent)
      target.addEventListener('keydown', markUserScrollIntent)
      return () => {
        target.removeEventListener('wheel', handleWheelIntent as EventListener)
        target.removeEventListener('touchmove', markUserScrollIntent)
        target.removeEventListener('pointerdown', markUserScrollIntent)
        target.removeEventListener('keydown', markUserScrollIntent)
      }
    },
    [handleWheelIntent, markUserScrollIntent],
  )

  const cancelActiveSmoothScroll = useCallback(
    (containerOverride?: HTMLElement) => {
      if (!smoothScrollActiveRef.current) return

      const container = containerOverride ?? getScrollContainer()
      if (container) {
        const scrollTop = container.scrollTop
        lastObservedScrollHeightRef.current = container.scrollHeight
        lastObservedScrollTopRef.current = setScrollTopImmediately(container, scrollTop)
      }
      smoothScrollActiveRef.current = false
    },
    [getScrollContainer],
  )

  const scrollContainerToBottom = useCallback(
    (behavior: ScrollBehaviorWithInstant): boolean => {
      const container = getScrollContainer()
      if (!enabled || !container || !contentAttachedRef.current) return false

      const top = container.scrollHeight
      lastObservedScrollHeightRef.current = top
      if (behavior === 'instant') {
        lastObservedScrollTopRef.current = setScrollTopImmediately(container, top)
        smoothScrollActiveRef.current = false
      } else {
        scrollContainer(container, top, behavior)
        smoothScrollActiveRef.current = behavior === 'smooth'
      }
      return true
    },
    [enabled, getScrollContainer],
  )

  const scheduleScrollToBottom = useCallback(
    (behavior: ScrollBehaviorWithInstant) => {
      scheduledScrollBehaviorRef.current = behavior
      if (scheduledScrollFrameRef.current !== undefined) return

      scheduledScrollFrameRef.current = requestAnimationFrame(() => {
        scheduledScrollFrameRef.current = undefined
        const scheduledBehavior = scheduledScrollBehaviorRef.current
        scheduledScrollBehaviorRef.current = undefined
        if (scheduledBehavior) scrollContainerToBottom(scheduledBehavior)
      })
    },
    [scrollContainerToBottom],
  )

  const updatePinnedState = useCallback(
    (container: HTMLElement) => {
      const scrollTop = container.scrollTop
      const scrollHeight = container.scrollHeight
      const scrollRangeShrank = scrollRangeShrankRef.current || scrollHeight < lastObservedScrollHeightRef.current
      const nearBottom = isNearBottom(container, bottomThreshold)
      const scrolledUp = scrollTop < lastObservedScrollTopRef.current
      const userScrolledUp = scrolledUp && userScrollIntentRef.current
      lastObservedScrollTopRef.current = scrollTop
      lastObservedScrollHeightRef.current = scrollHeight
      scrollRangeShrankRef.current = false

      if (scrolledUp && !nearBottom && (userScrolledUp || !scrollRangeShrank)) {
        const wasFollowing = autoFollowRef.current
        autoFollowRef.current = false
        cancelScheduledScroll()
        if (wasFollowing) cancelActiveSmoothScroll()
        clearUserScrollIntent()
        setIsAtBottom(nearBottom)
        return
      }

      if (scrolledUp && !nearBottom && autoFollowRef.current) {
        setIsAtBottom(true)
        return
      }

      if (nearBottom) {
        autoFollowRef.current = true
        if (isAtActualBottom(container)) smoothScrollActiveRef.current = false
      }
      setIsAtBottom(smoothScrollActiveRef.current || nearBottom)
    },
    [bottomThreshold, cancelActiveSmoothScroll, cancelScheduledScroll, clearUserScrollIntent],
  )

  const scrollToBottom = useCallback(
    (behaviorOrOptions: ScrollBehaviorWithInstant | ScrollToBottomOptions = 'smooth') => {
      const options = typeof behaviorOrOptions === 'string' ? {behavior: behaviorOrOptions} : behaviorOrOptions
      const behavior = options.behavior ?? 'smooth'
      const shouldRespectReducedMotion = options.respectReducedMotion ?? true
      const resolvedBehavior =
        shouldRespectReducedMotion && prefersReducedMotion && behavior === 'smooth' ? 'instant' : behavior

      autoFollowRef.current = true
      setIsAtBottom(true)
      scheduleScrollToBottom(resolvedBehavior)
    },
    [prefersReducedMotion, scheduleScrollToBottom],
  )

  useEffect(() => {
    cancelActiveSmoothScroll()
    autoFollowRef.current = autoFollowOnMount
    cancelScheduledScroll()
    scrollRangeShrankRef.current = false
    const container = getScrollContainer()
    if (container) {
      lastObservedScrollTopRef.current = container.scrollTop
      lastObservedScrollHeightRef.current = container.scrollHeight
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change, react-hooks/set-state-in-effect
      setIsAtBottom(isNearBottom(container, bottomThreshold))
    } else {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setIsAtBottom(false)
    }
  }, [
    autoFollowOnMount,
    bottomThreshold,
    cancelActiveSmoothScroll,
    cancelScheduledScroll,
    getScrollContainer,
    resetKey,
  ])

  const handleResize = useCallback(() => {
    const container = getScrollContainer()
    if (!enabled || !container || !contentAttachedRef.current) return
    if (container.scrollHeight < lastObservedScrollHeightRef.current) scrollRangeShrankRef.current = true
    lastObservedScrollHeightRef.current = container.scrollHeight

    if (autoFollowRef.current) {
      setIsAtBottom(true)
      const behavior =
        smoothScrollActiveRef.current || scheduledScrollBehaviorRef.current === 'smooth' ? 'smooth' : 'instant'
      scheduleScrollToBottom(behavior)
      return
    }

    const nearBottom = isNearBottom(container, bottomThreshold)
    setIsAtBottom(nearBottom)
  }, [bottomThreshold, enabled, getScrollContainer, scheduleScrollToBottom])

  const observeResize = useResizeObserver(handleResize)

  const containerCallbackRef = useCallback(
    (node: HTMLElement | null) => {
      if (!node || !enabled || scrollTarget !== 'container') return

      lastObservedScrollTopRef.current = node.scrollTop
      lastObservedScrollHeightRef.current = node.scrollHeight
      setIsAtBottom(isNearBottom(node, bottomThreshold))
      const handleScroll = () => updatePinnedState(node)
      const scrollListenerOptions = {passive: true} as AddEventListenerOptions

      // eslint-disable-next-line github/prefer-observers
      node.addEventListener('scroll', handleScroll, scrollListenerOptions)
      const stopListeningForIntent = addUserScrollIntentListeners(node)
      const stopObserving = observeResize(node)
      return () => {
        node.removeEventListener('scroll', handleScroll, scrollListenerOptions)
        stopListeningForIntent()
        stopObserving?.()
        cancelScheduledScroll()
        cancelActiveSmoothScroll(node)
      }
    },
    [
      bottomThreshold,
      cancelActiveSmoothScroll,
      cancelScheduledScroll,
      enabled,
      addUserScrollIntentListeners,
      observeResize,
      scrollTarget,
      updatePinnedState,
    ],
  )

  const contentCallbackRef = useCallback(
    (node: HTMLElement | null) => {
      if (!node || !enabled) return

      contentAttachedRef.current = true
      const container = getScrollContainer()
      if (container) {
        lastObservedScrollTopRef.current = container.scrollTop
        lastObservedScrollHeightRef.current = container.scrollHeight
        setIsAtBottom(isNearBottom(container, bottomThreshold))
      }
      const stopObserving = observeResize(node)
      return () => {
        contentAttachedRef.current = false
        stopObserving?.()
        cancelScheduledScroll()
        const currentContainer = getScrollContainer()
        if (currentContainer) cancelActiveSmoothScroll(currentContainer)
      }
    },
    [bottomThreshold, cancelActiveSmoothScroll, cancelScheduledScroll, enabled, getScrollContainer, observeResize],
  )

  useEffect(() => {
    if (!enabled || scrollTarget !== 'document') return
    const container = getScrollContainer()
    if (!container) return

    lastObservedScrollTopRef.current = container.scrollTop
    lastObservedScrollHeightRef.current = container.scrollHeight
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change, react-hooks/set-state-in-effect
    setIsAtBottom(isNearBottom(container, bottomThreshold))
    const handleScroll = () => updatePinnedState(container)
    const scrollListenerOptions = {passive: true} as AddEventListenerOptions

    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('scroll', handleScroll, scrollListenerOptions)
    const stopListeningForIntent = addUserScrollIntentListeners(window)
    const stopObserving = observeResize(container)
    return () => {
      window.removeEventListener('scroll', handleScroll, scrollListenerOptions)
      stopListeningForIntent()
      stopObserving?.()
      cancelScheduledScroll()
      cancelActiveSmoothScroll(container)
    }
  }, [
    bottomThreshold,
    cancelActiveSmoothScroll,
    cancelScheduledScroll,
    enabled,
    addUserScrollIntentListeners,
    getScrollContainer,
    observeResize,
    resetKey,
    scrollTarget,
    updatePinnedState,
  ])

  useEffect(
    () => () => {
      cancelScheduledScroll()
      cancelActiveSmoothScroll()
      clearUserScrollIntent()
    },
    [cancelActiveSmoothScroll, cancelScheduledScroll, clearUserScrollIntent],
  )

  return {
    isAtBottom,
    showScrollToBottom: !isAtBottom,
    scrollToBottom,
    containerCallbackRef,
    contentCallbackRef,
  } as const
}
